import { useRef, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  Settings,
  Building2,
  Users,
  Wallet,
  CalendarCheck,
  CalendarDays,
  Shield,
  LockKeyhole,
  Crown,
  Palette,
  Globe,
  FileText,
  Layers,
  Clock,
  CreditCard,
  UserCog,
  Hash,
  Sun,
  Bell,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  ChevronRight,
  Info,
  MapPin,
  Landmark,
  Briefcase,
  Calculator,
  ToggleLeft,
  Save,
  Eye,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";

const SECTIONS = [
  { id: "overview", label: "Settings Overview" },
  { id: "navigation", label: "Settings Navigation" },
  { id: "general", label: "General Settings" },
  { id: "org-profile", label: "Organisation Profile" },
  { id: "company-structure", label: "Company Structure" },
  { id: "financial-year", label: "Financial Year" },
  { id: "employee-setup", label: "Employee Setup" },
  { id: "payroll", label: "Payroll Settings" },
  { id: "attendance", label: "Attendance Settings" },
  { id: "leave", label: "Leave Settings" },
  { id: "statutory", label: "Statutory Setup" },
  { id: "security", label: "Security & Access" },
  { id: "subscription", label: "Subscription & Plans" },
  { id: "profile", label: "Profile & Preferences" },
  { id: "saving", label: "Saving & Updating" },
  { id: "faq", label: "FAQs" },
  { id: "errors", label: "Common Issues" },
];

const SETTINGS_CATEGORIES = [
  { icon: Building2, label: "Organisation Profile", accent: "indigo", items: ["Company Profile", "Financial Year", "Company Structure"], path: "/settings/organization/company-profile", roles: "Admin, HR" },
  { icon: CalendarCheck, label: "Attendance", accent: "emerald", items: ["Attendance Method", "Shift Management", "Roster Planner", "Attendance Rules", "Weekly Off", "Holiday Calendar", "Configuration Summary"], path: "/settings/attendance-leave-setup", roles: "Admin, HR" },
  { icon: CalendarDays, label: "Leave Settings", accent: "violet", items: ["Leave Types", "Leave Policies", "Leave Approval Workflow", "Leave Encashment"], path: "/settings/leave-setup", roles: "Admin, HR" },
  { icon: Wallet, label: "Payroll & Salary Components", accent: "blue", items: ["Guided Setup Wizard", "Payroll Home", "Payroll Settings", "Salary Components", "Formula Builder", "Salary Structures", "Pay Groups"], path: "/salary-v2/setup", roles: "Admin, HR" },
  { icon: Shield, label: "Statutory Setup", accent: "amber", items: ["PF", "ESI", "Professional Tax", "Labour Welfare Fund", "TDS"], path: "/statutory/pf", roles: "Admin, HR" },
  { icon: Users, label: "Employees Added", accent: "teal", items: ["Employment Types", "Employee Numbering"], path: "/settings/employee-configuration", roles: "Admin, HR" },
  { icon: LockKeyhole, label: "Security", accent: "purple", items: ["Users", "Roles", "Permissions"], path: "/settings/security/users-roles", roles: "Admin" },
  { icon: Globe, label: "General", accent: "slate", items: ["General Settings", "Subscription & Plans"], path: "/settings?tab=preferences", roles: "All roles" },
];

const SETTINGS_TABS = [
  { id: "general", label: "General", icon: Globe, desc: "Organisation name, timezone, currency, date format, employee code prefix", roles: "Admin, HR" },
  { id: "payroll", label: "Payroll", icon: Wallet, desc: "Salary structure, payroll cycle, payment day, processing day, working days, extra work pay, tax toggles", roles: "Admin, HR" },
  { id: "preferences", label: "Preferences", icon: Palette, desc: "Theme mode (System / Light / Dark), email and in-app notification toggles", roles: "All roles" },
  { id: "compliance", label: "Compliance", icon: Shield, desc: "Links to Statutory Console, PF Settings, ESI Settings, TDS Settings", roles: "Admin, HR" },
  { id: "access", label: "Security & Access", icon: LockKeyhole, desc: "Current role badge, link to Users & Roles management", roles: "Admin" },
];

const ORG_PROFILE_FIELDS = [
  { field: "Organisation Name", required: true, notes: "Displayed across the application" },
  { field: "Legal Name", required: false, notes: "Registered legal entity name" },
  { field: "PAN", required: false, notes: "Format: ABCDE1234L (validated)" },
  { field: "TAN", required: false, notes: "Minimum 10 characters" },
  { field: "State", required: true, notes: "Select from 32 Indian states" },
  { field: "Address", required: true, notes: "Registered office address" },
];

const ATTENDANCE_METHODS = [
  { method: "Default Present", code: "default_present", desc: "Mark all employees as present by default for each working day" },
  { method: "Manual Attendance", code: "manual_hr", desc: "Mark attendance manually for employees on a daily basis" },
  { method: "Excel Upload", code: "excel_upload", desc: "Upload attendance data using an Excel file" },
  { method: "Biometric", code: "biometric", desc: "Use biometric devices to capture employee attendance" },
];

const ATTENDANCE_RULES = [
  { rule: "Full Day Hours", default: "8.00", desc: "Minimum working hours for a full day" },
  { rule: "Half Day Hours", default: "4.00", desc: "Minimum working hours for a half day" },
  { rule: "Late Coming Threshold", default: "15 min", desc: "Minutes after which an employee is marked late" },
  { rule: "Early Exit Threshold", default: "15 min", desc: "Minutes before which an employee is marked early exit" },
  { rule: "Overtime Rule", default: "After Full Day", desc: "Options: After Full Day / Fixed Hours / Daily Threshold" },
];

const LEAVE_TYPES_FIELDS = [
  { field: "Leave Code", notes: "Required, max 10 chars, uppercase (e.g. CL). Cannot be changed after creation." },
  { field: "Leave Name", notes: "Required, max 120 chars (e.g. Casual Leave)" },
  { field: "Description", notes: "Optional short description" },
  { field: "Annual Limit (Days)", notes: "Number of days; leave blank for unlimited" },
  { field: "Paid Leave", notes: "Toggle Yes/No (default: Yes)" },
];

const IAM_TABS = [
  { tab: "Dashboard", desc: "Overview of IAM system, seed default roles" },
  { tab: "Users", desc: "User list with filters, invite wizard, bulk actions, role assignment" },
  { tab: "Roles", desc: "Create and manage roles with permissions, hierarchy, and data access scope" },
  { tab: "Permission Templates", desc: "Role access blueprints for page-level access control" },
  { tab: "Approval Workflows", desc: "Configure multi-level approval workflows" },
  { tab: "Access Policies", desc: "View and manage access policies" },
  { tab: "Session Management", desc: "View active sessions and revoke access" },
  { tab: "Audit Logs", desc: "Track all IAM-related actions and changes" },
];

const FAQ_DATA = [
  { q: "Where do I find Settings in BrixiGo?", a: "Click \"Settings\" in the sidebar or navigate to /settings. Admin and HR users see the full Settings Configuration Hub with all categories. Employees see only the Preferences tab." },
  { q: "What is the Settings Configuration Hub?", a: "The hub is the main settings landing page for admin/HR users. It shows all 8 settings categories as cards with search, progress tracking, and status badges (Configured / Setup needed / Recommended)." },
  { q: "How do I configure Organisation Profile?", a: "Go to Settings > Organisation Profile > Company Profile. Click Edit to update your organisation name, legal name, PAN, TAN, state, and address. Fields marked with * are required." },
  { q: "How do I set up the Financial Year?", a: "Go to Settings > Organisation Profile > Financial Year. Choose a cycle (April-March, January-December, or July-June), then create financial year entries with name, start date, and end date. Activate the year you want to use for payroll and reports." },
  { q: "Where do I configure Attendance Method?", a: "Go to Settings > Attendance > Attendance Method (or /settings/attendance-leave-setup?tab=method). Choose from Default Present, Manual Attendance, Excel Upload, or Biometric. Click Save Configuration to apply." },
  { q: "How do I create Leave Types?", a: "Go to Settings > Leave Settings > Leave Types (or /settings/leave-setup?tab=leave-types). Click Add Leave Type and fill in the Leave Code, Leave Name, Annual Limit, and Paid Leave toggle. The leave code cannot be changed after creation." },
  { q: "How do I manage Employment Types?", a: "Go to Settings > Employee Configuration > Employee Types (or /settings/employee-configuration#employee-types). You can add, edit, and delete employment types. The default employment type cannot be deleted." },
  { q: "How do I configure Employee Numbering?", a: "Go to Settings > Employee Configuration > Employee Numbering (or /settings/employee-configuration#employee-numbering). Enable Auto Generate, set the prefix (max 4 uppercase letters), optional suffix, starting number, and number length. A preview shows sample employee codes." },
  { q: "How do I set up Payroll Settings?", a: "Open Settings > Payroll tab. Configure the payroll cycle (Monthly/Bi-Weekly/Weekly), salary payment day, payroll processing day, and working days divisor. You can also enable extra work pay for over-duty, Sunday work, and holiday work with premium multipliers." },
  { q: "How do I manage Users and Roles?", a: "Go to Settings > Security & Access > Users & Roles (admin only). The IAM module has 8 tabs: Dashboard, Users, Roles, Permission Templates, Approval Workflows, Access Policies, Session Management, and Audit Logs." },
  { q: "How do I change my password?", a: "Go to /security (Security page). Enter your current password, then your new password twice. The password must score at least \"Good\" on the strength meter (8+ characters with mixed case, numbers, and symbols)." },
  { q: "Where can I manage my Subscription?", a: "Go to Settings > Subscription & Plans (admin only, or /settings/subscription). View your current plan, compare plans, manage billing and payments, and browse add-ons." },
  { q: "Can employees access Settings?", a: "Employees can only access the Preferences tab (theme and notifications), their Profile page, and the Security page (password change). All other settings require admin or HR access." },
];

const ERROR_DATA = [
  { error: "Organisation name is required", cause: "The organisation name field was left empty when saving Company Profile.", fix: "Enter your organisation name in the required field and save again.", severity: "error" },
  { error: "Invalid PAN (e.g. ABCDE1234F)", cause: "The PAN number does not match the required format: 5 uppercase letters, 4 digits, 1 uppercase letter.", fix: "Enter a valid PAN in the format ABCDE1234F.", severity: "error" },
  { error: "State is required", cause: "No state was selected in the Company Profile edit modal.", fix: "Select a state from the dropdown and save.", severity: "error" },
  { error: "Branch name is required", cause: "Tried to add or edit a branch without entering a name.", fix: "Enter a branch name (e.g. Head Office - Mumbai) and save.", severity: "error" },
  { error: "Department name is required", cause: "Tried to add or edit a department without entering a name.", fix: "Enter a department name (e.g. Human Resources) and save.", severity: "error" },
  { error: "Financial year name is required", cause: "The financial year name field was left empty.", fix: "Enter a name like FY 2026-27 and save.", severity: "error" },
  { error: "End date must be after start date", cause: "The end date selected is before or equal to the start date for a financial year.", fix: "Select an end date that falls after the start date.", severity: "error" },
  { error: "Dates overlap an existing financial year", cause: "The date range for the new financial year overlaps with an already existing one.", fix: "Adjust the start or end date to avoid overlap with existing financial years.", severity: "error" },
  { error: "An employment type with this name already exists", cause: "Tried to create an employment type with a name that is already in use.", fix: "Use a different name for the employment type.", severity: "warning" },
  { error: "The default employment type cannot be deleted", cause: "Tried to delete the system default employment type.", fix: "The default employment type is protected. You can create additional types but cannot remove the default.", severity: "warning" },
  { error: "Leave Code and Leave Name are required", cause: "Tried to save a leave type without filling in the mandatory fields.", fix: "Enter both the Leave Code and Leave Name before saving.", severity: "error" },
  { error: "Password is too weak", cause: "The new password scored below \"Good\" on the strength meter (score < 3 out of 5).", fix: "Use at least 8 characters with a mix of uppercase, lowercase, numbers, and special characters.", severity: "error" },
  { error: "New passwords do not match", cause: "The new password and confirm password fields have different values.", fix: "Re-enter the same password in both fields.", severity: "error" },
  { error: "Failed to save settings", cause: "The API call to save settings failed due to a network or server error.", fix: "Check your internet connection and try again. If the problem persists, contact support.", severity: "error" },
  { error: "Select at least one occurrence for the monthly off pattern", cause: "Monthly off pattern was enabled but no occurrence (1st, 2nd, 3rd, 4th, Last) was selected.", fix: "Select at least one occurrence checkbox (e.g. 2nd and 4th) for the monthly off pattern.", severity: "warning" },
  { error: "Workflow Name and Level 1 Approver are required", cause: "Tried to save a leave approval workflow without the mandatory fields.", fix: "Enter a workflow name and select a Level 1 Approver before saving.", severity: "error" },
];

export default function SettingsHelpPage() {
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const faqRef = useRef(null);

  const lc = search.toLowerCase();

  const filteredFaqs = useMemo(
    () => FAQ_DATA.filter((f) => !lc || f.q.toLowerCase().includes(lc) || f.a.toLowerCase().includes(lc)),
    [lc]
  );

  const filteredErrors = useMemo(
    () => ERROR_DATA.filter((e) => !lc || e.error.toLowerCase().includes(lc) || e.cause.toLowerCase().includes(lc) || e.fix.toLowerCase().includes(lc)),
    [lc]
  );

  const scrollTo = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div className="brix-page">
      {/* Hero */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-slate">
              <Settings size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Settings Guide</h1>
              <p className="hs-hero-sub">Understand BrixiGo system settings and configurations.</p>
            </div>
          </div>
          <form className="hs-search-wrap" onSubmit={(e) => { e.preventDefault(); if (search.trim() && faqRef.current) faqRef.current.scrollIntoView({ behavior: "smooth" }); }}>
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input type="text" className="hs-search-input" placeholder="Search — e.g. organisation, roles, payroll cycle..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      {/* Section Nav */}
      <div className="ah-section" style={{ padding: "16px 24px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {SECTIONS.map((s) => (
            <button key={s.id} type="button" className="hs-popular-tag" onClick={() => scrollTo(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* 1. Settings Overview */}
      <div className="ah-section" id="overview">
        <h2 className="ah-section-title"><Settings size={20} /> Settings Overview</h2>
        <p className="ah-section-desc">The Settings module is the central configuration hub for your BrixiGo organisation. It controls how your payroll, attendance, leave, compliance, and security systems behave.</p>

        <div className="ah-content-card">
          <h3>What Settings Controls</h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-card">
              <Building2 size={20} className="ah-feature-icon" style={{ color: "#6366f1" }} />
              <div className="ah-feature-name">Organisation Profile</div>
              <div className="ah-feature-desc">Company profile, financial year, branches, departments, designations</div>
            </div>
            <div className="ah-feature-card">
              <CalendarCheck size={20} className="ah-feature-icon" style={{ color: "#10b981" }} />
              <div className="ah-feature-name">Attendance</div>
              <div className="ah-feature-desc">Attendance method, shifts, rules, weekly off, holiday calendar</div>
            </div>
            <div className="ah-feature-card">
              <CalendarDays size={20} className="ah-feature-icon" style={{ color: "#8b5cf6" }} />
              <div className="ah-feature-name">Leave Settings</div>
              <div className="ah-feature-desc">Leave types, leave policies, approval workflows</div>
            </div>
            <div className="ah-feature-card">
              <Wallet size={20} className="ah-feature-icon" style={{ color: "#2563eb" }} />
              <div className="ah-feature-name">Payroll & Salary</div>
              <div className="ah-feature-desc">Payroll cycle, salary components, structures, pay groups</div>
            </div>
            <div className="ah-feature-card">
              <Shield size={20} className="ah-feature-icon" style={{ color: "#f59e0b" }} />
              <div className="ah-feature-name">Statutory Setup</div>
              <div className="ah-feature-desc">PF, ESI, Professional Tax, Labour Welfare Fund, TDS</div>
            </div>
            <div className="ah-feature-card">
              <Users size={20} className="ah-feature-icon" style={{ color: "#14b8a6" }} />
              <div className="ah-feature-name">Employee Setup</div>
              <div className="ah-feature-desc">Employment types, employee numbering configuration</div>
            </div>
            <div className="ah-feature-card">
              <LockKeyhole size={20} className="ah-feature-icon" style={{ color: "#a855f7" }} />
              <div className="ah-feature-name">Security</div>
              <div className="ah-feature-desc">Users, roles, permissions, sessions, audit logs</div>
            </div>
            <div className="ah-feature-card">
              <Globe size={20} className="ah-feature-icon" style={{ color: "#64748b" }} />
              <div className="ah-feature-name">General</div>
              <div className="ah-feature-desc">General settings, subscription & plans, preferences</div>
            </div>
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Role-Based Access</h3>
          <p>Different roles see different parts of Settings:</p>
          <div className="ah-info-grid" style={{ marginTop: 12 }}>
            <div className="ah-info-card">
              <span className="ah-status-badge" style={{ background: "#dbeafe", color: "#1e40af" }}>Admin</span>
              <span style={{ fontSize: "0.82rem", color: "#475569" }}>Full access to all settings, IAM, and subscription management</span>
            </div>
            <div className="ah-info-card">
              <span className="ah-status-badge" style={{ background: "#ede9fe", color: "#6d28d9" }}>HR</span>
              <span style={{ fontSize: "0.82rem", color: "#475569" }}>Access to organisation, attendance, leave, payroll, and employee setup</span>
            </div>
            <div className="ah-info-card">
              <span className="ah-status-badge" style={{ background: "#f1f5f9", color: "#475569" }}>Employee</span>
              <span style={{ fontSize: "0.82rem", color: "#475569" }}>Preferences tab only (theme and notification toggles)</span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Settings Navigation */}
      <div className="ah-section" id="navigation">
        <h2 className="ah-section-title"><Layers size={20} /> Settings Navigation</h2>
        <p className="ah-section-desc">The Settings landing page provides a Configuration Hub for admin and HR users. It organises all settings into 8 categories with search, progress tracking, and status badges.</p>

        <div className="ah-content-card">
          <h3>Settings Categories</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>The Settings Configuration Hub shows cards for each category. Each card displays status badges: <span className="ah-status-badge" style={{ background: "#dcfce7", color: "#166534" }}>Configured</span> <span className="ah-status-badge" style={{ background: "#fef3c7", color: "#92400e" }}>Setup needed</span> <span className="ah-status-badge" style={{ background: "#dbeafe", color: "#1e40af" }}>Recommended</span></p>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Category</th><th>Items</th><th>Access</th></tr>
            </thead>
            <tbody>
              {SETTINGS_CATEGORIES.map((cat) => (
                <tr key={cat.label}>
                  <td style={{ fontWeight: 600 }}>{cat.label}</td>
                  <td>{cat.items.join(", ")}</td>
                  <td><span className="ah-status-badge" style={{ background: "#f1f5f9", color: "#475569" }}>{cat.roles}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Hub Features</h3>
          <ul className="ah-manage-list">
            <li><Search size={14} /> <strong>Search bar</strong> — search across all settings items by name</li>
            <li><Layers size={14} /> <strong>Category sidebar</strong> — filter by category or view all settings</li>
            <li><CheckCircle2 size={14} /> <strong>Setup progress ring</strong> — shows percentage of settings configured</li>
            <li><Info size={14} /> <strong>Stats</strong> — total categories, settings count, configured count, payroll readiness</li>
          </ul>
        </div>
      </div>

      {/* 3. General Settings */}
      <div className="ah-section" id="general">
        <h2 className="ah-section-title"><Globe size={20} /> General Settings</h2>
        <p className="ah-section-desc">The Settings landing page (/settings) has 5 inline tabs. The General tab configures core organisation-wide settings.</p>

        <div className="ah-content-card">
          <h3>Settings Page Tabs</h3>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Tab</th><th>Description</th><th>Access</th></tr>
            </thead>
            <tbody>
              {SETTINGS_TABS.map((t) => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600 }}>{t.label}</td>
                  <td style={{ fontSize: "0.82rem" }}>{t.desc}</td>
                  <td><span className="ah-status-badge" style={{ background: "#f1f5f9", color: "#475569" }}>{t.roles}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>General Tab Fields</h3>
          <ul className="ah-manage-list">
            <li><Building2 size={14} /> <strong>Organisation name</strong> — your organisation display name</li>
            <li><Clock size={14} /> <strong>Timezone</strong> — Asia/Kolkata (default), UTC, Europe/London, America/New_York</li>
            <li><CreditCard size={14} /> <strong>Currency</strong> — INR (default), USD, EUR, GBP</li>
            <li><FileText size={14} /> <strong>Date format</strong> — DD/MM/YYYY (default), MM/DD/YYYY, YYYY-MM-DD</li>
            <li><Hash size={14} /> <strong>Employee code prefix</strong> — max 4 uppercase letters (admin only), e.g. EMP. New employees use this prefix by default.</li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Preferences Tab</h3>
          <ul className="ah-manage-list">
            <li><Sun size={14} /> <strong>Theme mode</strong> — System (default), Light, or Dark</li>
            <li><Bell size={14} /> <strong>Email notifications</strong> — toggle on/off</li>
            <li><Bell size={14} /> <strong>In-app notifications</strong> — toggle on/off</li>
          </ul>
          <div className="ah-info-card" style={{ marginTop: 12 }}>
            <Info size={14} style={{ color: "#2563eb", flexShrink: 0 }} />
            <span style={{ fontSize: "0.82rem", color: "#475569" }}>The Preferences tab is available to all roles, including employees.</span>
          </div>
        </div>
      </div>

      {/* 4. Organisation Profile */}
      <div className="ah-section" id="org-profile">
        <h2 className="ah-section-title"><Building2 size={20} /> Organisation Profile</h2>
        <p className="ah-section-desc">Configure your company identity, legal details, and address. Located at Settings &gt; Organisation Profile &gt; Company Profile.</p>

        <div className="ah-content-card">
          <h3>Company Profile Fields</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>The company profile shows read-only details by default. Click Edit to update.</p>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Field</th><th>Required</th><th>Notes</th></tr>
            </thead>
            <tbody>
              {ORG_PROFILE_FIELDS.map((f) => (
                <tr key={f.field}>
                  <td style={{ fontWeight: 600 }}>{f.field}</td>
                  <td>{f.required ? <span className="ah-status-badge" style={{ background: "#fee2e2", color: "#991b1b" }}>Required</span> : <span className="ah-status-badge" style={{ background: "#f1f5f9", color: "#64748b" }}>Optional</span>}</td>
                  <td style={{ fontSize: "0.82rem" }}>{f.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Validation Rules</h3>
          <ul className="ah-manage-list">
            <li><AlertTriangle size={14} /> PAN must match format: 5 uppercase letters + 4 digits + 1 letter (e.g. ABCDE1234F)</li>
            <li><AlertTriangle size={14} /> TAN must be at least 10 characters</li>
            <li><AlertTriangle size={14} /> Organisation name, state, and address are required</li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Setup Flow</h3>
          <div className="ah-workflow-steps">
            {["Company Profile", "Departments", "Designations", "Branches", "Dashboard"].map((step, i, arr) => (
              <div key={step} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="ah-status-badge" style={{ background: "#dbeafe", color: "#1e40af" }}>{i + 1}. {step}</span>
                {i < arr.length - 1 && <ArrowRight size={14} style={{ color: "#94a3b8" }} />}
              </div>
            ))}
          </div>
          <p style={{ marginTop: 8, fontSize: "0.82rem", color: "#64748b" }}>Complete each step in order to fully set up your organisation profile.</p>
        </div>
      </div>

      {/* 5. Company Structure */}
      <div className="ah-section" id="company-structure">
        <h2 className="ah-section-title"><Layers size={20} /> Company Structure</h2>
        <p className="ah-section-desc">Manage branches, departments, and designations. Located at Settings &gt; Organisation Profile &gt; Company Structure (admin only).</p>

        <div className="ah-content-card">
          <h3>Structure Sections</h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-card">
              <MapPin size={20} className="ah-feature-icon" style={{ color: "#10b981" }} />
              <div className="ah-feature-name">Branches</div>
              <div className="ah-feature-desc">Manage branch locations with name, state, city, and status. Auto-generated codes (BR001, BR002...)</div>
            </div>
            <div className="ah-feature-card">
              <Landmark size={20} className="ah-feature-icon" style={{ color: "#8b5cf6" }} />
              <div className="ah-feature-name">Departments</div>
              <div className="ah-feature-desc">Create departments with name and description. Auto-generated codes (DPT001, DPT002...)</div>
            </div>
            <div className="ah-feature-card">
              <Briefcase size={20} className="ah-feature-icon" style={{ color: "#f59e0b" }} />
              <div className="ah-feature-name">Designations</div>
              <div className="ah-feature-desc">Create designations with name and description. Auto-generated codes (DESG001, DESG002...)</div>
            </div>
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Actions Available</h3>
          <ul className="ah-manage-list">
            <li><CheckCircle2 size={14} /> <strong>Add</strong> — create new branches, departments, or designations via a side drawer</li>
            <li><Eye size={14} /> <strong>Edit</strong> — modify name, state/city (branches), or description</li>
            <li><ToggleLeft size={14} /> <strong>Status toggle</strong> — activate or deactivate entries</li>
            <li><AlertTriangle size={14} /> <strong>Delete</strong> — remove entries (confirmation required)</li>
          </ul>
        </div>
      </div>

      {/* 6. Financial Year */}
      <div className="ah-section" id="financial-year">
        <h2 className="ah-section-title"><Calculator size={20} /> Financial Year</h2>
        <p className="ah-section-desc">Configure financial year cycles used for payroll, TDS, and reports. Located at Settings &gt; Organisation Profile &gt; Financial Year.</p>

        <div className="ah-content-card">
          <h3>Financial Year Cycles</h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-card">
              <div className="ah-feature-name">April — March</div>
              <div className="ah-feature-desc">Standard Indian financial year (start month: April)</div>
            </div>
            <div className="ah-feature-card">
              <div className="ah-feature-name">January — December</div>
              <div className="ah-feature-desc">Calendar year cycle (start month: January)</div>
            </div>
            <div className="ah-feature-card">
              <div className="ah-feature-name">July — June</div>
              <div className="ah-feature-desc">Academic/mid-year cycle (start month: July)</div>
            </div>
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Managing Financial Years</h3>
          <ul className="ah-manage-list">
            <li><FileText size={14} /> <strong>Name</strong> — required, e.g. "FY 2026-27" (max 64 characters)</li>
            <li><CalendarDays size={14} /> <strong>Start Date &amp; End Date</strong> — both required; end date must be after start date</li>
            <li><CheckCircle2 size={14} /> <strong>Activate</strong> — makes a financial year active for payroll, TDS, and reports. A confirmation dialog appears before activation.</li>
            <li><AlertTriangle size={14} /> Dates cannot overlap with existing financial years</li>
          </ul>
        </div>
      </div>

      {/* 7. Employee Setup */}
      <div className="ah-section" id="employee-setup">
        <h2 className="ah-section-title"><UserCog size={20} /> Employee Setup</h2>
        <p className="ah-section-desc">Configure employment types and employee numbering. Located at Settings &gt; Employee Setup.</p>

        <div className="ah-content-card">
          <h3>Employment Types</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Manage the types of employment in your organisation (e.g. Full-time, Contract, Intern).</p>
          <ul className="ah-manage-list">
            <li><FileText size={14} /> <strong>Employment Type Name</strong> — required, max 64 characters</li>
            <li><FileText size={14} /> <strong>Description</strong> — optional, max 250 characters</li>
            <li><CheckCircle2 size={14} /> <strong>Status</strong> — toggle active/inactive</li>
            <li><AlertTriangle size={14} /> Duplicate names are not allowed</li>
            <li><AlertTriangle size={14} /> The default employment type cannot be deleted</li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Employee Numbering</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Configure how employee codes are automatically generated.</p>
          <ul className="ah-manage-list">
            <li><ToggleLeft size={14} /> <strong>Auto Generate</strong> — enable/disable automatic code generation</li>
            <li><Hash size={14} /> <strong>Prefix</strong> — required, max 4 uppercase letters (e.g. EMP)</li>
            <li><Hash size={14} /> <strong>Suffix</strong> — optional, max 4 uppercase letters</li>
            <li><Hash size={14} /> <strong>Starting Number</strong> — required, 1-9999</li>
            <li><Hash size={14} /> <strong>Number Length</strong> — required, 1-9 digits</li>
          </ul>
          <div className="ah-info-card" style={{ marginTop: 12 }}>
            <Info size={14} style={{ color: "#2563eb", flexShrink: 0 }} />
            <span style={{ fontSize: "0.82rem", color: "#475569" }}>A preview section shows sample generated codes (e.g. EMP0001, EMP0002, EMP0003, EMP0004...) so you can verify the format before saving.</span>
          </div>
        </div>
      </div>

      {/* 8. Payroll Settings */}
      <div className="ah-section" id="payroll">
        <h2 className="ah-section-title"><Wallet size={20} /> Payroll Settings</h2>
        <p className="ah-section-desc">Configure payroll cycle, payment schedule, and extra work pay. Located in the Settings page Payroll tab.</p>

        <div className="ah-content-card">
          <h3>Payroll Configuration</h3>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Setting</th><th>Options / Range</th><th>Description</th></tr>
            </thead>
            <tbody>
              <tr><td style={{ fontWeight: 600 }}>Default salary structure</td><td>Text input</td><td>Name of the default salary structure</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Payroll cycle</td><td>Monthly / Bi-Weekly / Weekly</td><td>Frequency of payroll processing</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Salary payment day</td><td>1-31</td><td>Day of month salary is paid</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Payroll processing day</td><td>1-31</td><td>Day payroll is processed each cycle</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Working days (salary divisor)</td><td>1-31</td><td>Fixed days to divide monthly gross (e.g. 21 or 26)</td></tr>
            </tbody>
          </table>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Extra Work Pay Settings</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Configure premium pay for work on special days. These settings are embedded in the Payroll tab.</p>
          <ul className="ah-manage-list">
            <li><ToggleLeft size={14} /> <strong>Pay over-duty days in salary</strong> — adds days above payable days to gross pay</li>
            <li><ToggleLeft size={14} /> <strong>Enable Sunday work premium</strong> — pay extra for Sunday work</li>
            <li><ToggleLeft size={14} /> <strong>Enable holiday work premium</strong> — pay extra for holiday work</li>
            <li><ToggleLeft size={14} /> <strong>Require HR approval before paying premium</strong> — admin reviews and approves in Daily Attendance</li>
          </ul>

          <div style={{ marginTop: 12 }}>
            <h4 style={{ fontSize: "0.84rem", fontWeight: 700, marginBottom: 8 }}>Premium Pay Multiplier Options</h4>
            <ul className="ah-manage-list">
              <li>2x salary (1 extra payable day)</li>
              <li>3x salary (2 extra payable days)</li>
              <li>1.5x salary (0.5 extra day)</li>
              <li>Custom extra days (0-5, step 0.5)</li>
            </ul>
          </div>

          <div className="ah-info-card" style={{ marginTop: 12 }}>
            <Info size={14} style={{ color: "#2563eb", flexShrink: 0 }} />
            <span style={{ fontSize: "0.82rem", color: "#475569" }}>You can also toggle "Pay premium when working on configured week-off days" and enable TDS, PF, and ESI toggles in the tax configurations section.</span>
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Payroll & Salary Setup (via Settings Hub)</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>The Settings Navigation also links to dedicated payroll setup pages:</p>
          <ul className="ah-manage-list">
            <li><ChevronRight size={14} /> <strong>Guided Setup Wizard</strong> — step-by-step payroll setup at /salary-v2/setup</li>
            <li><ChevronRight size={14} /> <strong>Salary Components</strong> — manage all salary components at /salary-v2/components</li>
            <li><ChevronRight size={14} /> <strong>Formula Builder</strong> — create salary formulas at /salary-v2/formula-builder</li>
            <li><ChevronRight size={14} /> <strong>Salary Structures</strong> — define pay structures at /salary-v2/structures</li>
            <li><ChevronRight size={14} /> <strong>Pay Groups</strong> — manage pay groups at /salary-v2/pay-groups</li>
          </ul>
        </div>
      </div>

      {/* 9. Attendance Settings */}
      <div className="ah-section" id="attendance">
        <h2 className="ah-section-title"><CalendarCheck size={20} /> Attendance Settings</h2>
        <p className="ah-section-desc">Configure how attendance is tracked, shifts, rules, and weekly offs. Located at Settings &gt; Attendance (5 tabs).</p>

        <div className="ah-content-card">
          <h3>Attendance Method</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Choose how attendance is recorded for your organisation. The selected method applies to all employees.</p>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Method</th><th>Description</th></tr>
            </thead>
            <tbody>
              {ATTENDANCE_METHODS.map((m) => (
                <tr key={m.code}>
                  <td style={{ fontWeight: 600 }}>{m.method}</td>
                  <td style={{ fontSize: "0.82rem" }}>{m.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Shift Management</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Create and manage work shifts with in/out times, break duration, and grace minutes.</p>
          <ul className="ah-manage-list">
            <li><FileText size={14} /> Fields: Shift Code, Shift Name, In Time, Out Time, Break Duration, Grace Minutes</li>
            <li><ToggleLeft size={14} /> Status cycle: Active → Draft → Inactive (click the status pill to cycle)</li>
            <li><Info size={14} /> Pagination: 10 shifts per page</li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Attendance Rules</h3>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Rule</th><th>Default</th><th>Description</th></tr>
            </thead>
            <tbody>
              {ATTENDANCE_RULES.map((r) => (
                <tr key={r.rule}>
                  <td style={{ fontWeight: 600 }}>{r.rule}</td>
                  <td>{r.default}</td>
                  <td style={{ fontSize: "0.82rem" }}>{r.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Weekly Off Configuration</h3>
          <ul className="ah-manage-list">
            <li><CalendarDays size={14} /> <strong>Sunday only</strong> — Sunday is the weekly off</li>
            <li><CalendarDays size={14} /> <strong>Saturday &amp; Sunday</strong> — both days are weekly offs</li>
            <li><CalendarDays size={14} /> <strong>Custom</strong> — choose specific days from a 7-day checkbox row</li>
          </ul>
          <div style={{ marginTop: 12 }}>
            <h4 style={{ fontSize: "0.84rem", fontWeight: 700, marginBottom: 8 }}>Monthly Off Pattern</h4>
            <ul className="ah-manage-list">
              <li><ToggleLeft size={14} /> Enable monthly off pattern for specific occurrences</li>
              <li><CalendarDays size={14} /> Select weekday and occurrences: 1st, 2nd, 3rd, 4th, Last</li>
              <li><Info size={14} /> Preview: e.g. "Every 2nd and 4th Saturday of the month will be an off day"</li>
            </ul>
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Configuration Summary</h3>
          <p style={{ fontSize: "0.82rem", color: "#64748b" }}>The Summary tab provides a read-only overview of attendance settings: Attendance Method, Shift Management, Attendance Rules, Weekly Off, and Holiday Calendar. Roster Planner and Holiday Calendar are separate pages — use Settings → Roster Planner for shift schedules and Holiday Calendar for organisation holidays.</p>
        </div>
      </div>

      {/* 10. Leave Settings */}
      <div className="ah-section" id="leave">
        <h2 className="ah-section-title"><CalendarDays size={20} /> Leave Settings</h2>
        <p className="ah-section-desc">Configure leave types, policies, and approval workflows. Located at Settings &gt; Leave Settings (3 tabs).</p>

        <div className="ah-content-card">
          <h3>Leave Types</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Define the types of leave available in your organisation.</p>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Field</th><th>Notes</th></tr>
            </thead>
            <tbody>
              {LEAVE_TYPES_FIELDS.map((f) => (
                <tr key={f.field}>
                  <td style={{ fontWeight: 600 }}>{f.field}</td>
                  <td style={{ fontSize: "0.82rem" }}>{f.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="ah-info-card" style={{ marginTop: 12 }}>
            <Info size={14} style={{ color: "#2563eb", flexShrink: 0 }} />
            <span style={{ fontSize: "0.82rem", color: "#475569" }}>Deleting a leave type archives it — existing balances and history are preserved.</span>
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Leave Policies</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Create policies that control leave allocation for each leave type.</p>
          <ul className="ah-manage-list">
            <li><FileText size={14} /> <strong>Policy Name</strong> — required (e.g. Regular Leave Policy)</li>
            <li><FileText size={14} /> <strong>Leave Type</strong> — required, select from existing types (cannot be changed after creation)</li>
            <li><Hash size={14} /> <strong>Annual Entitlement (Days)</strong> — number of days allocated per year</li>
            <li><ToggleLeft size={14} /> <strong>Carry Forward Allowed</strong> — toggle Yes/No (default: No)</li>
            <li><Clock size={14} /> <strong>Accrual Method</strong> — Monthly, Yearly, or As Earned (default: Monthly)</li>
            <li><CalendarDays size={14} /> <strong>Effective From</strong> — date the policy takes effect</li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Leave Approval Workflow</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Configure multi-level approval workflows for leave requests.</p>
          <ul className="ah-manage-list">
            <li><FileText size={14} /> <strong>Workflow Name</strong> — required (e.g. Standard Workflow)</li>
            <li><Users size={14} /> <strong>Level 1 Approver</strong> — required. Reporting Manager (from the org hierarchy) or any role configured in Users &amp; Roles</li>
            <li><ToggleLeft size={14} /> <strong>Level 2 Approval Required</strong> — toggle; when on, leave longer than the set number of days also needs Level 2 approval</li>
            <li><Users size={14} /> <strong>Level 2 Approver</strong> — an organization role (or the Reporting Manager). Every user assigned that role can approve</li>
            <li><ToggleLeft size={14} /> <strong>Status</strong> — Active/Inactive</li>
          </ul>
        </div>
      </div>

      {/* 11. Statutory Setup */}
      <div className="ah-section" id="statutory">
        <h2 className="ah-section-title"><Shield size={20} /> Statutory Setup</h2>
        <p className="ah-section-desc">Configure statutory compliance settings. Access via the Compliance tab on the Settings page or through the Settings hub links.</p>

        <div className="ah-content-card">
          <h3>Available Statutory Settings</h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-card">
              <div className="ah-feature-name">Provident Fund (PF)</div>
              <div className="ah-feature-desc">Configure PF at /statutory/pf. Access: Admin, HR.</div>
            </div>
            <div className="ah-feature-card">
              <div className="ah-feature-name">ESI</div>
              <div className="ah-feature-desc">Configure ESI at /statutory/esi. Access: Admin, HR.</div>
            </div>
            <div className="ah-feature-card">
              <div className="ah-feature-name">Professional Tax</div>
              <div className="ah-feature-desc">Configure PT at /statutory/pt. Access: Admin, HR.</div>
            </div>
            <div className="ah-feature-card">
              <div className="ah-feature-name">Labour Welfare Fund</div>
              <div className="ah-feature-desc">Configure LWF at /statutory/lwf. Access: Admin, HR. (Optional)</div>
            </div>
            <div className="ah-feature-card">
              <div className="ah-feature-name">TDS</div>
              <div className="ah-feature-desc">Configure TDS at /settings/tds. Access: Admin, HR.</div>
            </div>
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Compliance Tab Access</h3>
          <p style={{ fontSize: "0.82rem", color: "#64748b" }}>The Settings page Compliance tab provides quick links to the Statutory Console, PF Settings, ESI Settings, and TDS Settings. Only admin and HR users can access this tab. Non-admin/HR users see: "Statutory configuration requires admin or HR access."</p>
        </div>
      </div>

      {/* 12. Security & Access */}
      <div className="ah-section" id="security">
        <h2 className="ah-section-title"><LockKeyhole size={20} /> Security &amp; Access</h2>
        <p className="ah-section-desc">Manage passwords, users, roles, permissions, and sessions.</p>

        <div className="ah-content-card">
          <h3>Password &amp; Security (/security)</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Available to all authenticated users. Change your password and view session information.</p>
          <ul className="ah-manage-list">
            <li><LockKeyhole size={14} /> <strong>Change Password</strong> — enter current password, new password, and confirm</li>
            <li><Shield size={14} /> <strong>Password strength meter</strong> — scores 0-5 based on: length (8+ chars), uppercase, lowercase, digit, special character. Must score at least "Good" (3/5).</li>
            <li><Clock size={14} /> <strong>Session info</strong> — shows last login time and current session status</li>
          </ul>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Identity &amp; Access Management (Admin Only)</h3>
          <p style={{ marginBottom: 12, fontSize: "0.82rem", color: "#64748b" }}>Full IAM system at /settings/security/users-roles. Non-admin users see: "Admin access required."</p>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Tab</th><th>Description</th></tr>
            </thead>
            <tbody>
              {IAM_TABS.map((t) => (
                <tr key={t.tab}>
                  <td style={{ fontWeight: 600 }}>{t.tab}</td>
                  <td style={{ fontSize: "0.82rem" }}>{t.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 13. Subscription & Plans */}
      <div className="ah-section" id="subscription">
        <h2 className="ah-section-title"><Crown size={20} /> Subscription &amp; Plans</h2>
        <p className="ah-section-desc">Manage your BrixiGo subscription, billing, and add-ons. Admin only, located at Settings &gt; Subscription &amp; Plans.</p>

        <div className="ah-content-card">
          <h3>Subscription Tabs</h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-card">
              <div className="ah-feature-name">Overview</div>
              <div className="ah-feature-desc">Current plan, status, billing cycle, trial info, renewal date, usage meters, and enabled modules</div>
            </div>
            <div className="ah-feature-card">
              <div className="ah-feature-name">Plans &amp; Upgrade</div>
              <div className="ah-feature-desc">Compare plans, view pricing, feature comparison table, upgrade or contact sales</div>
            </div>
            <div className="ah-feature-card">
              <div className="ah-feature-name">Billing &amp; Payments</div>
              <div className="ah-feature-desc">Invoices, payment history, billing requests, and payment processing via Razorpay</div>
            </div>
            <div className="ah-feature-card">
              <div className="ah-feature-name">Add-ons</div>
              <div className="ah-feature-desc">Active add-ons and available add-on catalog with request option</div>
            </div>
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Billing Requests</h3>
          <p style={{ fontSize: "0.82rem", color: "#64748b" }}>You can send billing requests for: Upgrade, Downgrade, Cancel, Contact sales, or Other. Select a target plan and provide a message. Enterprise plans show a "Contact sales" option instead of direct upgrade.</p>
        </div>
      </div>

      {/* 14. Profile & Preferences */}
      <div className="ah-section" id="profile">
        <h2 className="ah-section-title"><Users size={20} /> Profile &amp; Preferences</h2>
        <p className="ah-section-desc">Manage your personal profile information. Available to all authenticated users at /profile.</p>

        <div className="ah-content-card">
          <h3>Profile Fields</h3>
          <ul className="ah-manage-list">
            <li><Users size={14} /> <strong>Profile Photo</strong> — upload an image (must be image type, max 3MB)</li>
            <li><FileText size={14} /> <strong>Name</strong> — required</li>
            <li><FileText size={14} /> <strong>Email</strong> — read-only, managed by your organisation admin</li>
            <li><FileText size={14} /> <strong>Phone</strong> — editable</li>
            <li><Building2 size={14} /> <strong>Organisation</strong> — read-only</li>
          </ul>
        </div>
      </div>

      {/* 15. Saving & Updating */}
      <div className="ah-section" id="saving">
        <h2 className="ah-section-title"><Save size={20} /> Saving &amp; Updating Settings</h2>
        <p className="ah-section-desc">How settings changes are persisted in BrixiGo.</p>

        <div className="ah-content-card">
          <h3>Save Workflow</h3>
          <div className="ah-workflow-steps">
            {["Open Settings", "Make Changes", "Click Save", "Validation Check", "Success Toast"].map((step, i, arr) => (
              <div key={step} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="ah-status-badge" style={{ background: "#dbeafe", color: "#1e40af" }}>{i + 1}. {step}</span>
                {i < arr.length - 1 && <ArrowRight size={14} style={{ color: "#94a3b8" }} />}
              </div>
            ))}
          </div>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Save Behaviour by Area</h3>
          <table className="ah-payroll-table">
            <thead>
              <tr><th>Settings Area</th><th>Save Method</th><th>Success Message</th></tr>
            </thead>
            <tbody>
              <tr><td>General tab</td><td>PATCH /organisation/me/hr-settings</td><td>"Settings saved successfully"</td></tr>
              <tr><td>Payroll tab</td><td>PATCH /organisation/me/hr-settings + setup status update</td><td>"Payroll settings saved — setup status updated"</td></tr>
              <tr><td>Organisation Profile</td><td>saveOrgProfileMinimal()</td><td>Success celebration overlay</td></tr>
              <tr><td>Branches / Departments / Designations</td><td>Individual CRUD APIs</td><td>"Branch added" / "Department updated" etc.</td></tr>
              <tr><td>Financial Year</td><td>PATCH with payroll.financial_years</td><td>"Financial year created" / "Financial year updated"</td></tr>
              <tr><td>Employee Setup</td><td>PATCH /organisation/me/hr-settings</td><td>"Employee numbering configuration saved"</td></tr>
              <tr><td>Attendance Settings</td><td>updateAttendanceConfiguration() / PATCH</td><td>"Attendance method saved successfully" / "Attendance rules saved"</td></tr>
              <tr><td>Leave Settings</td><td>Individual leave API calls</td><td>"Leave type created" / "Leave policy saved" etc.</td></tr>
            </tbody>
          </table>
        </div>

        <div className="ah-content-card" style={{ marginTop: 16 }}>
          <h3>Important Notes</h3>
          <ul className="ah-manage-list">
            <li><CheckCircle2 size={14} /> Save buttons are disabled when no changes have been made (not dirty)</li>
            <li><CheckCircle2 size={14} /> Validation runs before save — invalid fields show inline error messages</li>
            <li><CheckCircle2 size={14} /> A success celebration overlay appears after saving Organisation Profile ("Updated successfully — Changes apply organisation-wide")</li>
            <li><AlertTriangle size={14} /> If an API error occurs, a toast with the error detail is shown (e.g. "Failed to save settings")</li>
          </ul>
        </div>
      </div>

      {/* 16. FAQs */}
      <div className="ah-section" id="faq" ref={faqRef}>
        <h2 className="ah-section-title">Frequently Asked Questions</h2>
        <div className="hs-faq-list">
          {filteredFaqs.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <div key={faq.q} className="hs-faq-item">
                <button type="button" className="hs-faq-btn" onClick={() => setOpenFaq(isOpen ? null : idx)}>
                  <span className="hs-faq-q">{faq.q}</span>
                  <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: "hidden" }}>
                      <div className="hs-faq-answer">{faq.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
          {filteredFaqs.length === 0 && (
            <p style={{ textAlign: "center", color: "#94a3b8", padding: 24 }}>No FAQs match your search.</p>
          )}
        </div>
      </div>

      {/* 17. Common Issues */}
      <div className="ah-section" id="errors">
        <h2 className="ah-section-title"><AlertTriangle size={20} /> Common Settings Issues &amp; Solutions</h2>
        <p className="ah-section-desc">Actual error messages from BrixiGo Settings and how to resolve them.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filteredErrors.map((err) => (
            <div key={err.error} className="ah-error-card">
              <div className="ah-error-header">
                <span className={`ah-error-badge ${err.severity === "error" ? "ah-badge-error" : "ah-badge-warning"}`}>
                  {err.severity === "error" ? "Error" : "Warning"}
                </span>
                <code className="ah-error-code">{err.error}</code>
              </div>
              <div className="ah-error-body">
                <div><strong>Cause:</strong> {err.cause}</div>
                <div><strong>Fix:</strong> {err.fix}</div>
              </div>
            </div>
          ))}
          {filteredErrors.length === 0 && (
            <p style={{ textAlign: "center", color: "#94a3b8", padding: 24 }}>No errors match your search.</p>
          )}
        </div>
      </div>

    </div>
  );
}
